/* eslint-disable react/require-default-props */
import { Typography } from '@material-ui/core'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { gregorianToJalaali } from '../../helpers/date'
import { getTranslate, lang } from '../../localization'
import { DateRangePicker } from '../DatePicker/DateRangePicker'
import { DateRangePickerProps } from '../DatePicker/DateRangePickerComponents/types/DateRangePicker.types'
import useStyles from './index.style'

interface DatePickerInputWithLabelProps
  extends Omit<React.HTMLProps<HTMLInputElement>, 'error'> {
  label?: string
  error?: boolean
  helpertext?: string | undefined
  fullWidth?: boolean
  startIcon?: ReactNode
  noHelperText?: boolean
  inputSize?: 'small' | 'medium'
  ref?: React.Ref<HTMLInputElement>
  rightHeader?: React.ReactNode
  isForFilter?: boolean
  datePickerProps: DateRangePickerProps
  disabled?: boolean
}

export const DatePickerInputWithLabel = React.forwardRef(
  (props: DatePickerInputWithLabelProps, ref: React.Ref<HTMLInputElement>) => {
    const classes = useStyles()
    const {
      name,
      label,
      // className,
      error,
      helpertext,
      fullWidth,
      startIcon,
      noHelperText,
      inputSize = 'medium',
      rightHeader,
      isForFilter,
      datePickerProps,
      disabled
      // ...otherProps
    } = props
    const [datePickerIsOpen, setdatePickerIsOpen] = useState(false)
    const datePickerRef = useRef<HTMLDivElement | null>(null)
    const triggerRef = useRef<HTMLButtonElement | null>(null)

    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (
          datePickerRef.current &&
          !datePickerRef?.current?.contains(event.target as Node) &&
          datePickerIsOpen
        ) {
          setdatePickerIsOpen(false)
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [datePickerRef, datePickerIsOpen])

    /**
     * The Jalali half of a DateObject, converted from the Gregorian one when
     * the caller did not supply it.
     *
     * Every caller but one round-trips the whole DateObject that `onChange`
     * handed them, which carries both calendars. A caller holding its date as
     * a plain string can only reconstruct the Gregorian half — and under the
     * /fa route this component reads the Jalali half, so the display stayed on
     * "Select Date" forever while a perfectly good date sat in the form state.
     * The operator got no confirmation their choice registered.
     *
     * A fallback rather than a requirement on callers: the six existing ones
     * pass both and are unaffected, and a future caller supplying only one is
     * no longer silently wrong on half the estate's locales.
     */
    const toJalaali = (gregorian?: string) =>
      gregorian ? gregorianToJalaali(gregorian) : undefined

    const selctedDate = useMemo(() => {
      if (!datePickerProps.range) {
        return `${
          lang === 'fa'
            ? datePickerProps.dateObject?.jalaaliStartDate ||
              toJalaali(datePickerProps.dateObject?.gregorianStartDate) ||
              getTranslate('Select Date')
            : datePickerProps.dateObject?.gregorianStartDate ||
              getTranslate('Select Date')
        }`
      }
      return `${
        lang === 'fa'
          ? datePickerProps.dateObject?.jalaaliStartDate ||
            toJalaali(datePickerProps.dateObject?.gregorianStartDate) ||
            getTranslate('Start Date')
          : datePickerProps.dateObject?.gregorianStartDate ||
            getTranslate('Start Date')
      } => ${
        lang === 'fa'
          ? datePickerProps.dateObject?.jalaaliEndDate ||
            toJalaali(datePickerProps.dateObject?.gregorianEndDate) ||
            getTranslate('End Date')
          : datePickerProps.dateObject?.gregorianEndDate ||
            getTranslate('End Date')
      }`
    }, [
      datePickerProps.dateObject?.gregorianEndDate,
      datePickerProps.dateObject?.gregorianStartDate,
      datePickerProps.range
    ])

    return (
      <div
        className={`${classes.root} ${fullWidth ? classes.fullWidth : ''}`}
        ref={ref}>
        {label && (
          <div className={classes.inputHeader}>
            {label && (
              <label
                id={`${name}-label`}
                className={classes.label}
                htmlFor={name}>
                {label}
              </label>
            )}
            {rightHeader && (
              <div className={classes.rightHeader}>{rightHeader}</div>
            )}
          </div>
        )}
        <div
          className={classes.inputContainer}
          onClick={() => setdatePickerIsOpen(disabled ? false : true)}
          /* Escape closes the calendar and puts focus back on the trigger.
             Without it the only ways out were a mouse click on Done and a
             mousedown outside — both mouse-only. A keyboard user could open
             the calendar and then had no way to dismiss it, which is a worse
             position than the control being inert, because aria-expanded then
             asserts a state they cannot leave. */
          onKeyDown={(event) => {
            if (event.key === 'Escape' && datePickerIsOpen) {
              event.stopPropagation()
              setdatePickerIsOpen(false)
              triggerRef.current?.focus()
            }
          }}
          role="presentation"
          ref={datePickerRef}>
          {/* A button, where this was a div carrying an onClick.

              The <label htmlFor={name}> above pointed at nothing: the input it
              named was commented out and the value rendered in a plain div, so
              the label associated with no element and getByLabelText resolved
              to zero. The only way to open the picker was a mouse click on a
              role="presentation" wrapper, which is not reachable by keyboard
              at all.

              A button rather than the input that used to be here, because
              `selctedDate` falls back to placeholder text when no date is
              chosen — putting that in an input's `value` would make the
              placeholder BE the value. A button is a labelable element, so the
              htmlFor association is real, and Enter and Space raise a click
              that bubbles to the wrapper handler. That handler SETS open
              rather than toggling it, so the bubbled click cannot close what
              it just opened.

              The class list is unchanged so all six consumers render exactly
              as before; classes.valueButton only strips the browser's default
              button chrome. */}
          <button
            type="button"
            id={name}
            ref={triggerRef}
            disabled={disabled}
            /* "true", not "dialog". The popup carries no dialog role, no focus
               trap and no focus management, so naming it a dialog promises a
               contract it does not keep. Giving the calendar a real dialog
               role and a keyboard-navigable day grid is a change to the shared
               DateRangePicker and affects all six consumers — it is not this
               change. */
            aria-haspopup="true"
            aria-expanded={datePickerIsOpen}
            /* Names the label AND the value element, so the control
               announces "Trace window start 2026-08-28" rather than just its
               label. A <button> IS a labelable element, so <label htmlFor>
               alone already won the name computation — measured, the name was
               exactly "Trace window start" — which meant the SELECTED VALUE
               was never spoken.

               The value gets its own id rather than the button referencing
               itself: a self-reference contributes nothing to the computed
               name, which the first attempt at this established by failing. */
            aria-labelledby={
              label ? `${name}-label ${name}-value` : `${name}-value`
            }
            className={[
              classes.valueButton,
              inputSize === 'small'
                ? classes.selectedDateValueContainerSmall
                : '',
              isForFilter
                ? classes.selectedDateValueContainerForFilter
                : classes.selectedDateValueContainer,
              disabled ? classes.disabled : ''
            ].join(' ')}>
            <span id={`${name}-value`}>{selctedDate}</span>
          </button>
          <div className={classes.startIconContainer}>{startIcon}</div>
          <div className={classes.datePickerContainer}>
            {datePickerIsOpen && (
              <DateRangePicker
                {...datePickerProps}
                showDatePickerBox={datePickerIsOpen}
                onDoneClicked={(event) => {
                  event.stopPropagation()
                  setdatePickerIsOpen(false)
                }}
                type={lang === 'fa' ? 'jalaali' : 'gregorian'}
              />
            )}
          </div>
        </div>
        {!noHelperText && (
          <Typography
            variant="caption"
            className={[classes.helpertext, error ? classes.error : ''].join(
              ' '
            )}>
            {helpertext}
          </Typography>
        )}
      </div>
    )
  }
)
